import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { useSiteSetting } from "@/hooks/useSiteSetting";
import { useAffiliateRef } from "@/hooks/useAffiliateRef";

const ADMIN_PATHS = [
  "/dashboard", "/clinicas", "/painel-clinica", "/orcamentos", "/leads",
  "/internacoes", "/cupons", "/mapa-familia", "/mapa-atendentes", "/pesquisa",
  "/ambulancias", "/fichas", "/visitas", "/lembretes", "/documentos",
  "/sugestoes", "/notificacoes", "/perfil", "/usuarios", "/seo", "/anuncios",
];

const isAdminRoute = (pathname: string) =>
  ADMIN_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/"));

const WA_GREEN = "#25D366";

const messages = [
  { line1: "Precisa de ajuda?", line2: "Converse conosco" },
  { line1: "Está com dúvidas?", line2: "Fale com a gente" },
  { line1: "Podemos te ajudar!", line2: "Chame no WhatsApp" },
  { line1: "Quer saber mais?", line2: "Estamos online agora" },
  { line1: "Atendimento 24h", line2: "Tire suas dúvidas" },
];

const WA_DISMISSED_KEY = "wa-widget-dismissed";

export default function WhatsAppWidget() {
  const location = useLocation();
  const [msgIndex, setMsgIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(WA_DISMISSED_KEY) === "1"; } catch { return false; }
  });
  const [refPhone, setRefPhone] = useState<string | null>(null);
  const defaultPhone = useSiteSetting("whatsapp_phone", "5562994377238");
  const refUsername = useAffiliateRef();

  const onAdminRoute = isAdminRoute(location.pathname);

  // Fetch ref profile phone
  useEffect(() => {
    if (!refUsername) return;
    (async () => {
      try {
        const resp = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-ref-profile`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            },
            body: JSON.stringify({ username: refUsername }),
          }
        );
        if (resp.ok) {
          const data = await resp.json();
          if (data.telefone) setRefPhone(data.telefone);
        }
      } catch { /* ignore */ }
    })();
  }, [refUsername]);

  // Rotate messages
  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setMsgIndex((i) => (i + 1) % messages.length);
        setVisible(true);
      }, 400);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // On public pages, always show the site default phone (not the clinic's real contact)
  const phone = (() => {
    let p = (refPhone || defaultPhone).replace(/\D/g, "");
    if (p && !p.startsWith("55")) p = "55" + p;
    return p;
  })();

  const msg = messages[msgIndex];

  const handleDismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(WA_DISMISSED_KEY, "1"); } catch {}
  };

  if (onAdminRoute || dismissed) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2">
      {/* Close button */}
      <button
        onClick={handleDismiss}
        aria-label="Fechar WhatsApp"
        className="absolute -top-2 -right-2 z-10 w-6 h-6 rounded-full bg-muted border border-border shadow flex items-center justify-center hover:bg-destructive hover:text-destructive-foreground transition-colors"
      >
        <X className="h-3.5 w-3.5" />
      </button>
      {/* Text bubble */}
      <div
        className={`bg-card border border-border rounded-lg shadow-lg px-3 py-2 text-sm transition-all duration-300 ${
          visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-2"
        }`}
      >
        <span className="text-muted-foreground">{msg.line1}</span>
        <br />
        <strong className="text-foreground">{msg.line2}</strong>
      </div>

      {/* WhatsApp button */}
      <a
        href={`https://wa.me/${phone}`}
        target="_blank"
        rel="noopener noreferrer"
        className="w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-105"
        style={{ background: WA_GREEN }}
        aria-label="WhatsApp"
      >
        <svg width="30" height="30" viewBox="0 0 512 512">
          <path
            fill="#FEFEFE"
            d="M187.145,135.945l-16.772-0.883c-5.297,0-10.593,1.766-14.124,5.297c-7.945,7.062-21.186,20.303-24.717,37.959c-6.179,26.483,3.531,58.262,26.483,90.041s67.09,82.979,144.772,105.048c24.717,7.062,44.138,2.648,60.028-7.062c12.359-7.945,20.303-20.303,22.952-33.545l2.648-12.359c0.883-3.531-0.883-7.945-4.414-9.71l-55.614-25.6c-3.531-1.766-7.945-0.883-10.593,2.648l-22.069,28.248c-1.766,1.766-4.414,2.648-7.062,1.766c-15.007-5.297-65.324-26.483-92.69-79.448c-0.883-2.648-0.883-5.297,0.883-7.062l21.186-23.834c1.766-2.648,2.648-6.179,1.766-8.828l-25.6-57.379C193.324,138.593,190.676,135.945,187.145,135.945"
          />
          <path
            fill="none"
            stroke="#FEFEFE"
            strokeWidth="18"
            d="M255.117,44.138C137.71,44.138,44.138,137.71,44.138,254.234c0,40.607,11.476,80.331,32.662,113.876l-15.006,66.207l75.916-19.531c32.662,20.303,70.621,32.662,110.345,32.662c115.641,0,211.862-96.221,211.862-213.628S371.641,44.138,255.117,44.138z"
          />
        </svg>
      </a>
    </div>
  );
}
